import React, { useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Layout as AntLayout, 
  Menu, 
  Input, 
  Avatar, 
  Dropdown, 
  Button, 
  Space, 
  Badge,
  Breadcrumb
} from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
  DashboardOutlined,
  TeamOutlined,
  MedicineBoxOutlined,
  BankOutlined,
  CheckOutlined,
  CalendarOutlined,
  ContactsOutlined,
  CrownOutlined,
  FileTextOutlined,
  InsuranceOutlined
} from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { useTranslation } from '../hooks/useTranslation';
import LanguageSelector from './LanguageSelector';
import DebugAuth from './DebugAuth';

const { Header, Sider, Content } = AntLayout;
const { Search } = Input;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, loading } = useAuth();
  const { t } = useTranslation();
  const { 
    canManageUsers, 
    canManageDoctors, 
    canManageClinics, 
    canManageProviders, 
    canManageAppointments, 
    canManagePatients,
    canManageAdmins,
    canViewSystem,
    canViewLogs,
    canManageSettings,
    canViewInsurance
  } = usePermissions();

  // Debug: Log user and permissions
  console.log('Layout - User:', user);
  console.log('Layout - User permissions:', user?.permissions);
  console.log('Layout - Is super admin:', user?.is_super_admin);
  console.log('Layout - Loading:', loading);

  const navigation = useMemo(() => [
    { 
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: t('navigation.dashboard'),
      title: t('navigation.dashboard'),
      show: false // hide dashboard from navigation
    },
    { 
      key: '/appointments',
      icon: <CalendarOutlined />,
      label: t('navigation.appointments'),
      title: t('navigation.appointments'),
      show: canManageAppointments()
    },
    { 
      key: '/users',
      icon: <TeamOutlined />,
      label: t('navigation.users'),
      title: t('navigation.users'),
      show: canManageUsers()
    },
    { 
      key: '/doctors',
      icon: <MedicineBoxOutlined />,
      label: t('navigation.doctors'),
      title: t('navigation.doctors'),
      show: canManageDoctors()
    },
    { 
      key: '/clinics',
      icon: <BankOutlined />,
      label: t('navigation.clinics'),
      title: t('navigation.clinics'),
      show: canManageClinics()
    },
    { 
      key: '/providers',
      icon: <CheckOutlined />,
      label: t('navigation.providers'),
      title: t('navigation.providers'),
      show: canManageProviders()
    },
    { 
      key: '/patients',
      icon: <ContactsOutlined />,
      label: t('navigation.patients'),
      title: t('navigation.patients'),
      show: canManagePatients()
    },
    { 
      key: '/insurance',
      icon: <InsuranceOutlined />,
      label: t('navigation.insurance') || 'Insurance',
      title: t('navigation.insurance') || 'Insurance',
      show: canViewInsurance()
    },
    // { 
    //   key: '/claims',
    //   icon: <FileTextOutlined />,
    //   label: t('navigation.claims'),
    //   title: t('navigation.claims'),
    //   show: true // Claims always visible for now
    // },
    { 
      key: '/admin-users',
      icon: <CrownOutlined />,
      label: t('navigation.adminUsers'),
      title: t('navigation.adminUsers'),
      show: canManageAdmins()
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: t('common.settings'),
      title: t('common.settings'),
      show: canManageSettings()
    }
  ], [t, user, canManageUsers, canManageDoctors, canManageClinics, canManageProviders, canManageAppointments, canManagePatients, canManageAdmins, canManageSettings, canViewInsurance]);

  // Debug: Log navigation items and their visibility
  console.log('Layout - Navigation items:', navigation.map(item => ({
    key: item.key,
    label: item.label,
    show: item.show
  })));

  const handleLogout = () => {
    logout();
    // Redirect is now handled in the useAuth hook
  };

  const handleMenuClick = (e: any) => {
    if (e.key === 'logout') {
      handleLogout();
    } else if (e.key === 'settings') {
      navigate('/user-settings');
    } else if (e.key === 'profile') {
      // TODO: Navigate to profile page when implemented
      console.log('Profile clicked - not implemented yet');
    }
  };

  const userMenuItems = [
    // {
    //   key: 'profile',
    //   icon: <UserOutlined />,
    //   label: t('common.profile'),
    // },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('common.settings'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('common.logout'),
      danger: true,
    },
  ];

  const getCurrentTitle = () => {
    const currentItem = navigation.find(item => location.pathname.startsWith(item.key));
    if (location.pathname.startsWith('/insurance')) {
      const pathParts = location.pathname.split('/').filter(Boolean);
      if (pathParts.length > 1 && pathParts[1]) {
        // Format payor name
        const payorName = pathParts[1]
          .split('')
          .map((char, index) => {
            if (index === 0) return char.toUpperCase();
            if (char === char.toUpperCase() || pathParts[1][index - 1] === ' ') {
              return char;
            }
            return char;
          })
          .join('')
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/^./, (str) => str.toUpperCase());
        return payorName;
      }
      return currentItem?.title || 'Insurance';
    }
    return currentItem?.title || t('navigation.appointments');
  };

  const getBreadcrumbItems = () => {
    const currentItem = navigation.find(item => location.pathname.startsWith(item.key));
    const pathParts = location.pathname.split('/').filter(Boolean);
    
    const breadcrumbs = [
      {
        title: t('common.home')
      }
    ];

    // Handle insurance routes
    if (location.pathname.startsWith('/insurance')) {
      breadcrumbs.push({
        title: currentItem?.title || 'Insurance'
      });
      if (pathParts.length > 1 && pathParts[1]) {
        // Format payor name
        const payorName = pathParts[1]
          .split('')
          .map((char, index) => {
            if (index === 0) return char.toUpperCase();
            if (char === char.toUpperCase() || pathParts[1][index - 1] === ' ') {
              return char;
            }
            return char;
          })
          .join('')
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/^./, (str) => str.toUpperCase());
        breadcrumbs.push({
          title: payorName
        });
      }
    } else {
      breadcrumbs.push({
        title: currentItem?.title || t('navigation.appointments')
      });
    }

    return breadcrumbs;
  };

  // Show loading state while authentication is being checked
  if (loading) {
    return (
      <AntLayout style={{ minHeight: '100vh' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          background: '#f0f2f5'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', marginBottom: '16px' }}>Loading...</div>
            <div style={{ fontSize: '14px', color: '#666' }}>Checking authentication...</div>
          </div>
        </div>
      </AntLayout>
    );
  }

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        width={250}
        style={{
          background: '#001529',
        }}
      >
        <div style={{ 
          height: 64, 
          margin: 16, 
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 'bold',
          fontSize: collapsed ? 14 : 16
        }}>
          {collapsed ? 'CC' : 'Call Center'}
        </div>
        
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          style={{ borderRight: 0 }}
        >
          {!loading && user && (
            <>
              {navigation.filter(item => item.show).map((item) => (
                <Menu.Item key={item.key} icon={item.icon}>
                  <Link to={item.key}>{item.label}</Link>
                </Menu.Item>
              ))}
            </>
          )}
        </Menu>
      </Sider>
      
      <AntLayout>
        <Header 
          style={{ 
            padding: '0 24px', 
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 1px 4px rgba(0,21,41,.08)',
            height: '64px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: '16px',
                width: 64,
                height: 64,
              }}
            />
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 600, lineHeight: '64px' }}>
              {getCurrentTitle()}
            </h1>
          </div>
          
          <Space size="middle" align="center" style={{ height: '100%', alignItems: 'center' }}>
            <LanguageSelector />
            
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Search
                placeholder={t('common.search')}
                style={{ 
                  width: 250
                }}
                onSearch={value => setSearchQuery(value)}
              />
            </div>
            
            {/* <Badge count={3} size="small">
              <Button 
                type="text" 
                icon={<BellOutlined />} 
                size="large"
                style={{ fontSize: '16px' }}
              />
            </Badge>
             */}
            <Dropdown 
              menu={{ items: userMenuItems, onClick: handleMenuClick }}
              placement="bottomRight"
            >
              <Space style={{ cursor: 'pointer' }}>
                <Avatar 
                  style={{ backgroundColor: '#1890ff' }}
                  icon={<UserOutlined />}
                >
                  {user?.firstname?.[0]}{user?.lastname?.[0]}
                </Avatar>
                <span style={{ fontWeight: 500 }}>
                  {user?.firstname} {user?.lastname}
                </span>
              </Space>
            </Dropdown>
          </Space>
        </Header>
        
        <Content style={{ margin: '24px' }}>
          <div style={{ marginBottom: 16 }}>
            <Breadcrumb items={getBreadcrumbItems()} />
          </div>
          
          <div
            style={{
              padding: 24,
              minHeight: 360,
              background: '#fff',
              borderRadius: 8,
            }}
          >
            {children}
          </div>
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default Layout; 